/**
 * Keeps agent-provided form schemas behind a safe state and shape boundary before
 * they reach the interactive dynamic form.
 */
import React from 'react';
import Box from '@amzn/meridian/box';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { DynamicForm, VALID_FIELD_TYPES } from './DynamicForm';
import { type ChatMessageProps } from './types';
import { getUiToolRenderState } from './ui-tools';

const VALID_TYPES_SET = new Set<string>(VALID_FIELD_TYPES);

interface NormalizedField { name: string; label: string; type: string; [key: string]: unknown }

const isNonEmptyString = (value: unknown): value is string => (
  typeof value === 'string' && value.trim().length > 0
);

const normalizeOptionalStrings = (
  value: Readonly<Record<string, unknown>>,
  keys: readonly string[],
): Record<string, unknown> => {
  const optionalKeys = new Set(keys);
  return Object.fromEntries(
    Object.entries(value).filter(([key, optionalValue]) => (
      !optionalKeys.has(key) || isNonEmptyString(optionalValue)
    )),
  );
};

function normalizeField (f: unknown): NormalizedField | null {
  if (f == null || typeof f !== 'object') return null;
  const field = f as Record<string, unknown>;
  if (typeof field.name !== 'string' || !field.name) return null;
  if (typeof field.label !== 'string' || !field.label) return null;
  const type = typeof field.type === 'string' && VALID_TYPES_SET.has(field.type) ? field.type : 'text';
  return {
    ...normalizeOptionalStrings(field, ['description']),
    name: field.name,
    label: field.label,
    type,
  };
}

const getSubmittedValues = (
  isLastMessage: boolean,
  nextMessage: ChatMessageProps['nextMessage'],
): Readonly<Record<string, unknown>> | undefined => {
  if (isLastMessage || nextMessage?.role !== 'user' || nextMessage.parts.length !== 1) return undefined;
  const [part] = nextMessage.parts;
  if (part.type !== 'text' || !part.text.startsWith('{') || !part.text.endsWith('}')) return undefined;

  try {
    const parsed: unknown = JSON.parse(part.text);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Readonly<Record<string, unknown>>
      : undefined;
  } catch {
    return undefined;
  }
};

export const FormMessage: React.FC<ChatMessageProps> = ({ part, index, isLastMessage, isLoading, nextMessage, onSend }) => {
  const { t } = useTranslation();
  const renderState = getUiToolRenderState(part.state, isLastMessage, isLoading);
  if (renderState === 'loading') {
    return (
      <div data-testid="collect-fields-loading" key={index} style={{ alignItems: 'center', display: 'flex', gap: '8px', marginTop: '8px' }}>
        <Loader size="small" />
        <Text color="secondary" type="b100">{t('chat_form_preparing')}</Text>
      </div>
    );
  }
  if (renderState === 'error') {
    return <Text color="error" data-testid="collect-fields-error" key={index} type="b100">{t('chat_form_error')}</Text>;
  }
  if (renderState !== 'available') return null;

  const schema = (part.input ?? part.output) as Record<string, unknown> | undefined;
  if (!isNonEmptyString(schema?.title) || !Array.isArray(schema.fields) || schema.fields.length === 0) return null;
  const normalizedFields = schema.fields.map(normalizeField).filter((f): f is NonNullable<typeof f> => f !== null);
  if (normalizedFields.length === 0) return null;
  const normalizedSchema = {
    ...normalizeOptionalStrings(schema, ['description', 'submitLabel', 'secondaryLabel']),
    title: schema.title,
    fields: normalizedFields,
  };
  const submittedValues = getSubmittedValues(isLastMessage, nextMessage);
  return (
    <Box data-testid="chat-form" key={index} spacingInset="400" type="outline" width="100%">
      <DynamicForm
        disabled={!isLastMessage || isLoading}
        schema={normalizedSchema as Parameters<typeof DynamicForm>[0]['schema']}
        {...(submittedValues ? { submittedValues } : {})}
        onSubmit={(data) => { onSend(JSON.stringify(data)); }}
      />
    </Box>
  );
};
